import React, { createContext, useState, useContext, useEffect } from 'react';
import { message } from 'antd';
import api from '../services/api';
import { getLoginPath, shouldStartLocalAuth } from '../utils/localAuth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');


    if (urlToken) {
      localStorage.setItem('token', urlToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${urlToken}`;
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchUser();
    } else if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else if (shouldStartLocalAuth({ pathname: window.location.pathname })) {
      window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/auth/local`;
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await api.get('/auth/user');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user - Full error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      // Don't automatically logout on error - let user stay on page
      // logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (forceAccountSelection = false) => {
    const loginPath = getLoginPath();
    let authUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${loginPath}`;
    if (forceAccountSelection && loginPath === '/auth/google') {
      authUrl += '?prompt=select_account';
    }
    window.location.href = authUrl;
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint if needed
      await api.get('/auth/logout').catch(() => {});
    } catch (error) {
      // Ignore errors - we're logging out anyway
    }
    
    // Clear all auth data
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    
    // Clear any other cached data
    localStorage.clear();
    sessionStorage.clear();
    
    message.info('Logged out successfully');
    
    // Force reload to clear any cached state
    window.location.href = '/';
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
